import * as React from 'react';

import { type Breadcrumb, GenericHeroImage } from '@lendi-jam/generic-hero-image';
import HeroImage, { type HeroImageButton, type HeroImageReason } from '@lendi-jam/hero-image';
import { embedMarketingTrackingIntoURL } from '@lendi-jam/jam-link';
import * as AllIcons from '@lendi-ui/icon';
import parse, { domToReact } from 'html-react-parser';

import { HERO_BUTTON } from '#contentTypes/currentLandingPage/HeroImage';
import { GenericHeroImageContent } from '#contentTypes/GenericHeroImage';

const IconRef: { [key: string]: React.FC<AllIcons.IconProps> } = AllIcons;

interface PrismicHeroImageProps {
  component: GenericHeroImageContent;
  setCurrentUrl?: React.Dispatch<React.SetStateAction<string>>;
  setModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  shouldOpenModal?: boolean;
  breadcrumbs?: Breadcrumb[];
  hideBreadcrumbs?: boolean;
}

const PrismicHeroImage: React.FC<PrismicHeroImageProps> = ({
  component,
  setCurrentUrl,
  shouldOpenModal,
  setModalOpen,
  breadcrumbs,
}) => {
  const {
    alignment,
    background_color,
    component_type,
    hero_component_image_mobile,
    hero_component_image,
    hero_image_disclaimer,
    hero_image_subtitle,
    hero_image_title,
    hide_breadcrumbs,
    lendi_logo_variant,
    partner_logo,
    partner_logo_height,
    primary_button_link,
    primary_button,
    secondary_button_link,
    secondary_button,
  } = component.primary;
  const PrimaryButton = primary_button.text ? { text: primary_button.text, link: embedMarketingTrackingIntoURL(primary_button_link?.url) } : undefined;
  const SecondaryButton = secondary_button.text
    ? { text: secondary_button.text, link: embedMarketingTrackingIntoURL(secondary_button_link?.url) }
    : undefined;

  if (component_type === 'HeroBanner') {
    interface HeroFieldsType {
      HeroButtons: HeroImageButton[];
      HeroReasons: HeroImageReason[];
    }

    const { HeroButtons, HeroReasons } = component.items.reduce(
      (prev: HeroFieldsType, item) => {
        if (item.additional_component_type === HERO_BUTTON) {
          const iconKey = item.component_icon;
          const iconUrl = embedMarketingTrackingIntoURL(item.button_link.url);
          const Icon = IconRef.hasOwnProperty(iconKey) ? IconRef[iconKey] : undefined;
          const button = {
            icon: <span>{Icon && <Icon color="shade.0" width="2.5rem" height="2.5rem" />}</span>,
            text: item.component_text.text,
            url: iconUrl || (typeof window !== 'undefined' ? embedMarketingTrackingIntoURL(window.location.href) : ''),
            onClick: () => {
              if (shouldOpenModal && setCurrentUrl && setModalOpen) {
                setCurrentUrl(iconUrl || window.location.href);
                setModalOpen(true);
              } else {
                window.location.href = iconUrl;
              }
            },
          };
          prev.HeroButtons.push(button);
        } else {
          const reason = {
            text: item.component_text.text,
            mobileVisible: item.visible_on_mobile,
          };
          prev.HeroReasons.push(reason);
        }
        return prev;
      },
      { HeroButtons: [], HeroReasons: [] }
    );

    return (
      <HeroImage
        alt={hero_component_image?.alt}
        background={hero_component_image?.fluid?.src}
        backgroundSharp={hero_component_image?.fluid}
        buttons={HeroButtons}
        mobileImage={hero_component_image_mobile?.fluid}
        backgroundColor={background_color}
        disclaimer={hero_image_disclaimer?.text}
        lendiLogoVariant={lendi_logo_variant}
        partnerLogo={partner_logo?.fluid}
        partnerLogoHeight={partner_logo_height}
        alignment={alignment || 'center'}
        reasons={HeroReasons}
        subtitle={parse(hero_image_subtitle.html, {
          replace: ({ name, attribs, children }) => {
            if (name === 'a') {
              return <a href={embedMarketingTrackingIntoURL(attribs?.href)}>{children && domToReact(children)}</a>;
            }
            if (name === 'h2') {
              return <>{children[0]?.data}</>; // TODO: CUT-335
            }
            return null;
          },
        })}
        title={hero_image_title.text}
      />
    );
  }

  return (
    <GenericHeroImage
      alt={hero_component_image?.alt}
      title={hero_image_title.text}
      subtitle={parse(hero_image_subtitle.html)}
      imageSharp={hero_component_image?.fluid}
      breadcrumbs={breadcrumbs}
      hideBreadcrumbs={hide_breadcrumbs}
      primaryButton={PrimaryButton}
      secondaryButton={SecondaryButton}
    />
  );
};

export default PrismicHeroImage;
